import React, {useState} from "react";
import {inject, observer} from "mobx-react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
//local
import {showSuccess} from "helpers/notifications.helper";
import {toProjectId, PROJECTS} from "constants/routes.const";
import {ConfirmModal} from "components/ConfirmModal";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {PublicPFPPublishModal} from "components/modals/PublicPFPPublishModal";
import CustomRadioSwitch from "components/CustomRadioSwitch";
import MiniSelect from "components/selects/MiniSelect";
import {CustomTooltip} from "components/CustomTooltip";
import {IconTrashCan, IconRocketUp, IconBullHorn, IconRefresh} from "components/icons";
import {VideoInstructionLink} from "components/VideoInstructionLink";
import {turnoverMiniSelect} from "constants/select.consts";
import {rfpStatuses} from "constants/buyer.consts";
import {LockValue} from "components/LockValue";
import {hasPermission} from "helpers/helper";

const WaitSaveButton = inject("store")(
  observer(({store: {buyer, auth}, inText, addClass, isSaveButton = false}) => {
    const {
      currentRequest,
      isFilledRequiredRFPFields,
      isPublishedRequest,
      newPublishedRFPRecipients,
      isDraftSaved,
    } = buyer;
    const {user} = auth;
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [isPublicPFPPublishModal, setPublicPFPPublishModal] = useState(false);
    const isDraft = currentRequest.status === rfpStatuses.DRAFT;

    const checkRecipients = async () => {
      if (!Object.values(currentRequest.recipients).flat().length)
        ConfirmModal({
          title: t("WANT_SAVE_WITHOUT_RECIPIENTS"),
          text: t("CANT_GET_ANY_OFFERS"),
          okText: "CANCEL_SELECT_NOW",
          cancelText: "DO_THIS_LATER",
          noCancelButton: true,
          type: "warning",
          onOk: () => buyer.setActiveRequestTab(1),
          onCancel: clickPublish,
          noCancelBefore: true,
        });
      else clickPublish();
    };

    const clickPublish = () =>
      ConfirmModal({
        title: t("WANT_SET_STATUS"),
        text: t("CANT_DELETE_RECIPIENTS"),
        type: "warning",
        onOk: () =>
          buyer
            .saveCurrentRequest(currentRequest.id, true)
            .then((data) =>
              buyer
                .changeRequestStatus(currentRequest?.id, rfpStatuses.PUBLISHED)
                .then(() => toggleToViewMode(data.message))
            )
            .finally(() => setLoading(false)),
      });

    const clickRemove = () =>
      ConfirmModal({
        title: t("WANT_DELETE_RFP"),
        text: t("CANT_RETURN"),
        type: "warning",
        onOk: () =>
          buyer
            .deleteRequest(currentRequest.id)
            .then(() =>
              navigate(
                currentRequest.ProjectId
                  ? toProjectId(currentRequest.ProjectId)
                  : PROJECTS
              )
            ),
      });

    const toggleToViewMode = (successMessage) => {
      showSuccess(isDraft ? t("SUCCESSFULLY_SAVED_PUBLISHED") : t(successMessage));
      buyer.setEditStatus(false);
      buyer.setNewRFPStatus(false);
      buyer.setActiveRequestTab(0);
      setLoading(false);
      buyer.resetRfpRecipientsFilterParams();
    };

    const handleChangeRequest = (field, value, needSaveRequest) => {
      buyer.updateCurrentRequestInfo({[field]: value}, !!needSaveRequest);
    };

    const saveRequest = async () => {
      // fill newPublishedRFPRecipients object if RFP in PUBLISHED status
      const newRFPRecipients = {
        ...newPublishedRFPRecipients,
        numberOrganizations: isDraft
          ? currentRequest.recipients?.registered?.length
          : currentRequest.recipients?.registered?.filter(
              (item) =>
                !newPublishedRFPRecipients.notRemoveRegistered
                  .map((value) => value.id)
                  .includes(item.id)
            ).length,
        numberEmails: isDraft
          ? currentRequest.recipients?.notRegistered?.length
          : currentRequest.recipients?.notRegistered?.filter(
              (item) => !newPublishedRFPRecipients.notRemoveNotRegistered.includes(item)
            ).length,
      };
      buyer.setNewPublishedRFPRecipients(newRFPRecipients);

      !currentRequest.ContactPersonId &&
        buyer.updateCurrentRequestInfo({ContactPersonId: user.id});

      if (isDraft && !currentRequest.isPublic) checkRecipients();
      else {
        const data = await buyer
          .saveCurrentRequest(currentRequest.id, true)
          .finally(() => setLoading(false));
        if (isDraft) setPublicPFPPublishModal(true);
        else toggleToViewMode(data.message);
      }
    };

    const isOnlySave = isSaveButton || currentRequest.status === rfpStatuses.PUBLISHED;

    return (
      <React.Fragment>
        <div
          className={`request_head__btn ${inText ? "in-text-position" : ""} ${
            addClass ?? ""
          }`}
        >
          {!isOnlySave && user.ActiveOrganization?.allowPublicRft && (
            <div className="df-row-center mb-3">
              <VideoInstructionLink
                label="VIDEO_LINK_RFP_EDIT_PUBLIC_RFP_LABEL"
                link="VIDEO_LINK_RFP_EDIT_PUBLIC_RFP_LINK"
                uppercase
                bold
                addClass="mr-4"
              />
              <div className="request_head__btn__status-row">
                <LockValue
                  canPermissions={["ENABLE_SWITCH_OFF_OPEN_RFT_TOGGLE"]}
                  needLabel
                  addClassIcon="open-rft-switch-locked-icon"
                  needShowModalToSubscribe
                >
                  <CustomRadioSwitch
                    name="isPublic"
                    checked={currentRequest.isPublic}
                    uppercase
                    small
                    addWrapperClass="mr-1"
                    isActive={(checked) =>
                      hasPermission(["ENABLE_SWITCH_OFF_OPEN_RFT_TOGGLE"]) &&
                      handleChangeRequest("isPublic", checked, isDraft)
                    }
                  />
                </LockValue>

                <CustomTooltip text="CREATE_RFP_PUBLIC_RFP_SWITCH_LABEL">
                  <div className="df hght-21">
                    <IconBullHorn />
                  </div>
                </CustomTooltip>
                <CustomTooltip text="CREATE_RFP_PUBLIC_RFP_SELECT_LABEL">
                  <div className="df ml-4 mr-1 hght-21">
                    <IconRefresh />
                  </div>
                </CustomTooltip>
                <MiniSelect
                  list={turnoverMiniSelect}
                  label=" "
                  selectedValue={buyer.minRevenue}
                  defaultValue={buyer.minRevenue}
                  fullWidthDropdownLabel
                  itemAsObject
                  onChange={(value) => handleChangeRequest("minRevenue", value, isDraft)}
                  informationText="CREATE_RFP_PUBLIC_RFP_BETA_INFO_LABEL"
                />
              </div>
            </div>
          )}
          <ButtonTransparent
            nonTransparent
            name={t(isPublishedRequest ? "SAVE_REQUEST" : "SAVE_AS_DRAFT")}
            type="button"
            color="green"
            onClick={() => {
              setLoading(true);
              setTimeout(saveRequest, 1100);
            }}
            icon={<IconRocketUp />}
            loading={loading}
            addClass="save-publish-button"
            disabled={!isFilledRequiredRFPFields}
          />
          {!isOnlySave && currentRequest.status === rfpStatuses.DRAFT && !inText && (
            <div className="df-row-jce-center mt-1">
              {isDraftSaved && (
                <span className="__uppercase fz-12 red">{t("RFP_EDIT_DRAFT_SAVED")}</span>
              )}
              <ButtonTransparent
                name="REMOVE"
                noBorder
                color="red"
                small
                icon={<IconTrashCan />}
                onClick={clickRemove}
              />
            </div>
          )}
          {!isOnlySave && !user.ActiveOrganization?.allowPublicRft && (
            <VideoInstructionLink
              label="VIDEO_LINK_RFP_EDIT_DISABLED_PUBLIC_RFPS_LABEL"
              link="VIDEO_LINK_RFP_EDIT_DISABLED_PUBLIC_RFPS_LINK"
              uppercase
              bold
              addClass="mt-4"
            />
          )}
        </div>
        {isPublicPFPPublishModal && (
          <PublicPFPPublishModal
            onSave={() =>
              buyer
                .changeRequestStatus(currentRequest.id, rfpStatuses.PUBLISHED)
                .then(() => toggleToViewMode())
            }
            onClose={() => {
              setPublicPFPPublishModal(false);
              setLoading(false);
            }}
          />
        )}
      </React.Fragment>
    );
  })
);

export default WaitSaveButton;
